import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const LEGAL_CONTENT = {
  terms: {
    title: "Terms of Service",
    lastUpdated: "February 17, 2026",
    content: `
## 1. Acceptance of Terms

By accessing and using Fruit Crush ("the Game"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Game.

## 2. Description of Service

Fruit Crush is a match-3 puzzle game that allows users to:
- Play through various levels and worlds
- Compete on leaderboards
- Purchase in-game items and subscriptions
- Interact with other players

## 3. User Accounts

### 3.1 Registration
To access certain features, you must create an account. You agree to:
- Provide accurate and complete information
- Maintain the security of your account
- Notify us immediately of any unauthorized access

### 3.2 Account Termination
We reserve the right to suspend or terminate accounts that:
- Violate these terms
- Engage in cheating or exploitation
- Harass other users

## 4. In-Game Purchases

### 4.1 Virtual Currency
- Stars and Coins are virtual currencies with no real-world value
- All purchases are final and non-refundable unless required by law
- Virtual items cannot be transferred or sold

### 4.2 Subscriptions
- Battle Pass subscriptions are billed monthly
- You may cancel at any time through your account settings
- Refunds are handled according to platform policies

## 5. User Conduct

You agree not to:
- Use cheats, exploits, or unauthorized third-party software
- Harass, abuse, or harm other users
- Attempt to gain unauthorized access to the Game or other accounts
- Use the Game for any illegal purpose

## 6. Intellectual Property

All content in Fruit Crush, including graphics, gameplay, and music, is owned by us or our licensors. You may not copy, modify, or distribute any content without permission.

## 7. Disclaimer of Warranties

The Game is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free service.

## 8. Limitation of Liability

To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Game.

## 9. Changes to Terms

We may update these terms at any time. Continued use of the Game after changes constitutes acceptance of the new terms.

## 10. Contact

For questions about these terms, please contact us through the Help Center.
    `
  },
  privacy: {
    title: "Privacy Policy",
    lastUpdated: "February 17, 2026",
    content: `
## 1. Information We Collect

### 1.1 Information You Provide
- Email address
- Player name
- Payment information (processed by Stripe)

### 1.2 Information Collected Automatically
- Game progress and statistics
- Device information
- Usage data

## 2. How We Use Your Information

We use your information to:
- Provide and improve the Game
- Process transactions
- Send important notifications
- Prevent fraud and abuse
- Comply with legal obligations

## 3. Information Sharing

We do not sell your personal information. We may share information with:
- Service providers (e.g., payment processors)
- Legal authorities when required by law
- Business partners with your consent

## 4. Data Security

We implement appropriate technical and organizational measures to protect your data, including:
- Encryption of sensitive data
- Secure authentication
- Regular security assessments

## 5. Your Rights

Depending on your location, you may have the right to:
- Access your personal data
- Correct inaccurate data
- Delete your data
- Export your data
- Opt out of marketing communications

## 6. Data Retention

We retain your data for as long as your account is active or as needed to provide services. You may request deletion of your account through the Help Center.

## 7. Children's Privacy

Fruit Crush is not intended for children under 13. We do not knowingly collect data from children under 13.

## 8. International Transfers

Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place.

## 9. Changes to This Policy

We may update this policy periodically. We will notify you of significant changes through the Game or email.

## 10. Contact

For privacy-related inquiries, please contact us through the Help Center.
    `
  },
  cookies: {
    title: "Cookie Policy",
    lastUpdated: "February 17, 2026",
    content: `
## 1. What Are Cookies?

Cookies are small text files stored on your device when you use our Game. They help us provide a better experience.

## 2. Types of Cookies We Use

### 2.1 Essential Cookies
Required for the Game to function properly:
- Authentication tokens
- Session management
- Security features

### 2.2 Functional Cookies
Remember your preferences:
- Language settings
- Game settings (music, sound effects)
- Display preferences

### 2.3 Analytics Cookies
Help us understand how you use the Game:
- Pages visited
- Features used
- Performance metrics

## 3. Local Storage

In addition to cookies, we use local storage to:
- Save game progress locally
- Cache game assets for faster loading
- Store authentication tokens

## 4. Third-Party Cookies

Some third-party services may set their own cookies:
- Stripe (payment processing)
- Analytics services

## 5. Managing Cookies

You can control cookies through:
- Browser settings
- Device settings
- Our cookie preferences (where available)

Note: Disabling essential cookies may prevent the Game from working properly.

## 6. Changes to This Policy

We may update this cookie policy as needed. Check back periodically for updates.

## 7. Contact

For questions about our use of cookies, please contact us through the Help Center.
    `
  }
};

export default function LegalPage({ page }) {
  const navigate = useNavigate();
  
  const content = LEGAL_CONTENT[page];
  
  if (!content) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Page Not Found</h1>
          <Button onClick={() => navigate("/")} className="bg-purple-600 hover:bg-purple-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🍓</span>
            <span className="text-lg font-bold text-white">Fruit Crush</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-slate-800/50 rounded-2xl border border-white/10 p-8">
          <h1 className="text-3xl font-bold text-white mb-2">{content.title}</h1>
          <p className="text-slate-400 text-sm mb-8">Last updated: {content.lastUpdated}</p>
          
          <div className="prose prose-invert prose-slate max-w-none">
            {content.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-xl font-bold text-white mt-8 mb-4">{line.replace('## ', '')}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={i} className="text-lg font-semibold text-purple-400 mt-6 mb-3">{line.replace('### ', '')}</h3>;
              }
              if (line.startsWith('- ')) {
                return <li key={i} className="text-slate-300 ml-4">{line.replace('- ', '')}</li>;
              }
              if (line.trim() === '') {
                return null;
              }
              return <p key={i} className="text-slate-300 mb-3">{line}</p>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
